<script setup lang="ts">
import {ref, watch} from "vue";
import {CircleCheck, Printer, TriangleAlert} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/core/components/ui/dialog";
import {useToastStore} from "@/stores/toast.store";
import {appointmentApi} from "@/container";
import {apiErrorMessage, isAuthorizationError} from "@/core/http/apiErrorMessage";

/**
 * What is left to do once the journey has been written.
 *
 * The screen underneath is spent: its plan has been programmed, and pressing
 * « Programmer le parcours » a second time would book the patient twice. So
 * every way out of this window — the button, the cross, the escape key —
 * leads back to the agenda.
 */
const {count, customerId, recapNotice} = defineProps<{
    count: number;
    customerId: number;
    /** Why the patient was not sent their recap, when they were not. */
    recapNotice: string | null;
}>();

const emits = defineEmits<{
    /** The pharmacist is done here; the caller leaves the screen. */
    (e: 'done'): void;
}>();

const open = defineModel<boolean>('open', {required: true});

const toastStore = useToastStore();
const exporting = ref(false);

watch(open, (isOpen) => {
    if (!isOpen) {
        emits('done');
    }
});

/**
 * The patient's appointments as a printable sheet.
 *
 * The same export as the patient file's own « Imprimer le récap des RDV »,
 * and deliberately so: it carries everything the patient has coming, not just
 * the acts of this journey, which is what they want to leave the counter
 * with. Printed rather than texted, for the patient with no mobile.
 */
const print = async () => {
    exporting.value = true;

    try {
        const blob = await appointmentApi().exportCustomerAppointments(customerId, {active: true});
        const url = window.URL.createObjectURL(blob);

        window.open(url, '_blank');
        window.URL.revokeObjectURL(url);
    } catch (error) {
        if (!isAuthorizationError(error)) {
            toastStore.showError({content: apiErrorMessage(error)});
        }
    } finally {
        exporting.value = false;
    }
};
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="max-w-md">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <CircleCheck class="size-5 shrink-0 text-secondary" />
          Parcours programmé
        </DialogTitle>
        <DialogDescription>
          {{ count }} rendez-vous {{ count > 1 ? 'ont été ajoutés' : 'a été ajouté' }} à votre agenda.
        </DialogDescription>
      </DialogHeader>

      <p
        v-if="recapNotice"
        class="flex items-start gap-1.5 rounded-lg bg-orange/20 px-2.5 py-2 text-xs leading-snug text-gray-dark"
      >
        <TriangleAlert class="mt-px size-3.5 shrink-0 text-orange" />
        {{ recapNotice }}
      </p>
      <p v-else class="text-xs leading-snug text-gray-500">
        Le patient a reçu le récapitulatif de son parcours.
      </p>

      <p class="text-xs leading-snug text-gray-500">
        Vous pouvez lui remettre le récapitulatif imprimé de ses rendez-vous à venir.
      </p>

      <DialogFooter>
        <Button variant="outline" class="gap-2" :disabled="exporting" @click="print">
          <Printer class="size-4" />
          Imprimer le récapitulatif
        </Button>
        <Button class="bg-primary" @click="open = false">
          Retour à l'agenda
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
